import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from '@happymoneyinc/styled-components';
import { fluidRange } from 'polished';
import ThemeWrapper from '../utils/ThemeWrapper/index.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import SvgIcon from '../SvgIcon/SvgIcon.js';

const Select = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    autoFocus,
    defaultOption,
    options,
    optional,
    id,
    label,
    onChange,
    value,
    testID
  } = props;
  return /*#__PURE__*/jsx(ThemeWrapper, {
    children: /*#__PURE__*/jsxs(SelectContainer, {
      "data-testid": testID,
      ref: ref,
      children: [/*#__PURE__*/jsxs(Top, {
        children: [/*#__PURE__*/jsx(StyledLabel, {
          htmlFor: id,
          children: label
        }), optional && /*#__PURE__*/jsx(OptionalText, {
          "data-testid": `optional-${testID}`,
          children: "Optional"
        })]
      }), /*#__PURE__*/jsxs(StyledSelect, {
        id: id,
        autoFocus: autoFocus,
        "aria-label": label,
        onChange: onChange,
        name: label,
        value: value,
        children: [!!defaultOption && /*#__PURE__*/jsx("option", {
          value: "",
          "data-testid": `default-option-${testID}`,
          children: defaultOption
        }), options.map(option => /*#__PURE__*/jsx("option", {
          value: option.value,
          children: option.label
        }, `${id}-${option.value}`))]
      }), /*#__PURE__*/jsx(Caret, {
        type: "openDownCaret"
      })]
    })
  });
});
const Caret = styled(SvgIcon)`
  bottom: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing[5];
}};
  position: absolute;
  right: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.spacing[5];
}};
  color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.gray[3];
}};
  path {
    fill: currentColor !important;
  }
`;
const SelectContainer = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.screen.lg;
}}) {
    height: auto;
    margin-bottom: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.spacing[4];
}};
  }
`;
const Top = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  left: 0;
  padding: ${_ref6 => {
  return '9px 18px';
}};
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
`;
const topStyles = css`
  color: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.gray[2];
}};
  font-family: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return theme.fontStyles.label.fontFamily;
}};
`;
const StyledLabel = styled.label`
  ${topStyles};
  font-size: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return `${theme.fontStyles.label.fontSize}px`;
}};
`;
const OptionalText = styled.span`
  ${topStyles};
  font-size: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return `${theme.fontStyles.paragraphs.xxs.fontSizeMin}px`;
}};
`;
const StyledSelect = styled.select`
  ${_ref11 => {
  let {
    theme
  } = _ref11;
  return fluidRange({
    prop: 'font-size',
    fromSize: `${theme.fontStyles.paragraphs.lg.fontSizeMin}px`,
    toSize: `${theme.fontStyles.paragraphs.lg.fontSizeMax}px`
  }, `${theme.screen.xxs}`, `${theme.screen.xl}`);
}}
  line-height: 1.5;
  appearance: none;
  background: ${_ref12 => {
  let {
    disabled,
    theme
  } = _ref12;
  return disabled ? theme.colors.gray[5] : theme.colors.white;
}};
  border-radius: 3px;
  border: ${_ref13 => {
  let {
    theme
  } = _ref13;
  return `2px solid ${theme.colors.gray[4]}`;
}};
  color: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.colors.black;
}};
  cursor: pointer;
  font-family: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.fonts.MabryProRegular;
}};
  height: 100%;
  min-height: 72px;
  outline: none;
  padding: ${_ref16 => {
  let {
    theme
  } = _ref16;
  return `${theme.spacing[6]} ${theme.spacing[6]} ${theme.spacing[2]} ${theme.spacing[4]}`;
}};
  transition: border 250ms;
  width: 100%;

  :hover {
    border: ${_ref17 => {
  let {
    theme
  } = _ref17;
  return `2px solid ${theme.colors.black}`;
}};
  }

  :focus {
    border: ${_ref18 => {
  let {
    theme
  } = _ref18;
  return `2px solid ${theme.colors.black}`;
}};
    outline: ${_ref19 => {
  let {
    theme
  } = _ref19;
  return `1px dashed ${theme.colors.flamingo[1]}`;
}};
    outline-offset: 1px;
  }

  :hover, :focus, :active {
    + ${Caret} {
      color: ${_ref20 => {
  let {
    theme
  } = _ref20;
  return theme.colors.black;
}};
    }
  }
`;
Select.defaultProps = {
  autoFocus: false,
  defaultOption: '',
  id: '',
  label: '',
  onChange: null,
  optional: false,
  options: [],
  value: undefined,
  testID: 'select'
};
Select.propTypes = {
  autoFocus: PropTypes.bool,
  defaultOption: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  optional: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  })),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  testID: PropTypes.string
};
var Select$1 = Select;

export { Select$1 as default };
//# sourceMappingURL=Select.js.map
